import React, { useState, useRef, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { useHistory } from 'react-router-dom';
import 'components/admin/customers/CSVDataEditor.scss';
import CentsLoader from 'components/common/centsLoader/CentsLoader';
import { uploadFile } from 'api/admin/customers/customers';

const CSVDataEditor = () => {
  const history = useHistory();
  const [addressIsChecked, setAddressIsChecked] = useState(false);
  const [logsIsChecked, setLogsIsChecked] = useState(false);
  const [downloadLinks, setDownloadLinks] = useState([]);
  const [logLinks, setLogLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [rowsPerChunk, setRowsPerChunk] = useState('1000');
  const messageRef = useRef(null);
  const logSection = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFileName, setSelectedFileName] = useState(null);

  const handleRowsPerChunkChange = (event) => {
    setRowsPerChunk(event.target.value);
  };

  const handleAddressCheckboxChange = () => {
    setAddressIsChecked((prevState) => !prevState);
  };

  const handleLogsCheckboxChange = () => {
    setLogsIsChecked((prevState) => !prevState);
  };

  const handleDragAndDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setSelectedFile(file);
      setSelectedFileName(file.path);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop: handleDragAndDrop });

  let inputAreaLabel = 'Drag and drop a CSV file here, or click to select a file';
  if (isDragActive) {
    inputAreaLabel = 'Drop the file here...';
  } else if (selectedFileName) {
    inputAreaLabel = `Selected file: ${selectedFileName}`;
  }

  const handleUploadCustomersShortcut = () => {
    history.push(`/customers/upload`);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setMessage('');
    setDownloadLinks([]);
    setLogLinks([]);

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('rows_per_chunk', rowsPerChunk);
    formData.append('execute_addresses_function', addressIsChecked ? 'on' : 'off');
    formData.append('execute_logs_function', logsIsChecked ? 'on' : 'off');

    const csvdataeditorApi = import.meta.env.VITE_CSVDATAEDITOR_BASE_URL;

    try {
      const response = await uploadFile(formData);
      setLoading(false);

      if (response.data.download_urls) {
        setMessage(response.data.message);
        const links = response.data.download_urls.map((url, index) => ({
          href: url,
          text: `Download part ${index + 1}`,
        }));
        setDownloadLinks(links);
      } else {
        throw new Error('No download URLs were returned.');
      }

      if (setLogsIsChecked && response.data.log_files) {
        const logLinks = response.data.log_files.map((logName) => ({
          href: `${csvdataeditorApi}/download_logs/${logName}`,
          text: `Download log: ${logName}`,
        }));
        setLogLinks(logLinks);
      }
    } catch (error) {
      setLoading(false);
      setMessage(`Error: ${error.message}`);
    }
  };

  return (
    <div className="scrollable-container">
      <div className="csv-data-editor-header">
        <h1>Upload a File</h1>
      </div>
      <div className="create-cents-customer-shourtcut">
        <button
          className="create-cents-customer-shourtcut-buttom"
          onClick={handleUploadCustomersShortcut}
        >
          Upload Customer List
        </button>
      </div>
      <form onSubmit={handleSubmit}>
        <div {...getRootProps()} className="dropzone">
          <input {...getInputProps()} />
          <p>{inputAreaLabel}</p>
        </div>
        <div className="rows_per_chunk_container">
          <label className="rows_per_chunk_input">
            Rows per chunk (optional, default 1000):
            <input
              type="number"
              value={rowsPerChunk}
              onChange={handleRowsPerChunkChange}
              min={'1'}
              placeholder="Enter number of rows"
            />
          </label>
        </div>
        <div className="execute_addresses_function_container">
          <label className="execute_addresses_function_label">
            <input
              type="checkbox"
              name="execute_addresses_function"
              onChange={handleAddressCheckboxChange}
            />
            The file has addresses that have to be separated
          </label>
          {addressIsChecked && (
            <div className="execute_addresses_function_message" ref={messageRef}>
              Note: Please ensure that the address column includes a header, as the column name will
              be used to identify the addresses within the file.
            </div>
          )}
        </div>
        <div className="execute_logs_function_container">
          <label className="execute_logs_function_label">
            <input
              type="checkbox"
              name="execute_logs_function"
              onChange={handleLogsCheckboxChange}
            />
            Check this box if you want to generate logs for this file
          </label>
        </div>

        {message && <div ref={messageRef}>{message}</div>}

        {downloadLinks.length > 0 && (
          <div className="file_download_links_container">
            <h3>Download Files:</h3>
            <ul>
              {downloadLinks.map((link, index) => (
                <li key={link.href}>
                  <a href={link.href} target="_blank" rel="noopener noreferrer">
                    {link.text}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}

        {logsIsChecked && logLinks.length > 0 && (
          <div className="logs_download_links_container" ref={logSection}>
            <h3>Log Files:</h3>
            <ul>
              {logLinks.map((log, index) => (
                <li key={log.href}>
                  <a href={log.href} target="_blank" rel="noopener noreferrer">
                    {log.text}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}

        <button className="submit_button" type="submit" disabled={loading}>
          {loading ? 'Uploading...' : 'Upload'}
        </button>
      </form>

      {loading && (
        <div className="loader-container">
          <CentsLoader />
        </div>
      )}
    </div>
  );
};

CSVDataEditor.displayName = 'CSVDataEditor';

export default CSVDataEditor;
